/**
 * AnalyticsModelEditDialog.js
 * Dialog d'édition/création de modèle — structure en étapes (Stepper).
 */
import React from "react";
import {
    Accordion, AccordionDetails, AccordionSummary,
    Alert, Autocomplete, Box, Button, Chip, CircularProgress, Collapse,
    Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel,
    IconButton, LinearProgress, Paper, Stack, Step, StepLabel, Stepper, Switch, TextField, Tooltip, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import BuildIcon from "@mui/icons-material/Build";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import RefreshIcon from "@mui/icons-material/Refresh";
import ApiAnalyticsEngineCtrl from "../../../../api/ApiAnalyticsEngineCtrl";
import AnsiText from "../../../../components/AnsiText";
import {SeriesRow, ParamRow, OutputRow} from "./editor/ModelFormRows";
import ImageOrCodeSection from "./editor/ImageOrCodeSection";

const ANALYSIS_TYPE_MAP = {
    "CORRELATION":       {groupId: "correlation",        groupLabel: "Correlation"},
    "DISTANCE":          {groupId: "distance",           groupLabel: "Distance & Similarite"},
    "STATS_TEST":        {groupId: "stats-tests",        groupLabel: "Tests statistiques"},
    "ANOMALY_DETECTION": {groupId: "anomaly-detection",  groupLabel: "Detection d'anomalies"},
    "TREND_ANALYSIS":    {groupId: "trend-analysis",     groupLabel: "Analyse de tendance"},
    "SHAPE_SIMILARITY":  {groupId: "shape-similarity",   groupLabel: "Similarite de forme"},
    "LAG_CORRELATION":   {groupId: "lag-correlation",    groupLabel: "Correlation par lag"},
    "SEASONALITY":       {groupId: "seasonality",        groupLabel: "Saisonnalite"},
    "STATIONARITY":      {groupId: "stationarity",       groupLabel: "Stationnarite"},
    "DISTRIBUTION_SHIFT":{groupId: "distribution-shift", groupLabel: "Changement de distribution"},
    "CLUSTERING":        {groupId: "clustering",         groupLabel: "Clustering"},
};
const ANALYSIS_TYPES = Object.keys(ANALYSIS_TYPE_MAP);
const KNOWN_REGISTRIES = ["registry.gitlab.com/blange/inria2025", "docker.io", "ghcr.io"];
const STEPS = ["Identite", "Configuration", "Code source", "Build"];

const BUILD_STATUS_CONFIG = {
    NONE:        {icon: <HourglassTopIcon fontSize="small"/>, color: "text.disabled", label: "Aucun build"},
    PENDING:     {icon: <HourglassTopIcon fontSize="small"/>, color: "warning.main", label: "En attente..."},
    BUILDING:    {icon: <BuildIcon fontSize="small"/>,        color: "info.main",    label: "Build en cours..."},
    SUCCESS:     {icon: <CheckCircleIcon fontSize="small"/>,  color: "success.main", label: "Build reussi"},
    FAILED:      {icon: <ErrorIcon fontSize="small"/>,        color: "error.main",   label: "Build echoue"},
    TIMEOUT:     {icon: <ErrorIcon fontSize="small"/>,        color: "warning.main",  label: "Timeout"},
    DISAPPEARED: {icon: <ErrorIcon fontSize="small"/>,        color: "error.main",   label: "Job disparu"},
};

/** Regex DNS-1035 : minuscules, chiffres et tirets, pas de début/fin par tiret */
const DNS_LABEL_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
/** Regex version : minuscules, chiffres, points et tirets */
const VERSION_RE = /^[a-z0-9][a-z0-9.\-]*$/;
/** Transforme une chaîne en slug DNS-1035 compatible */
function toDnsLabel(s) { return s.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/^-+|-+$/g, "").replace(/-{2,}/g, "-"); }

function emptyModel() {
    return {
        id: "", label: "", description: "", version: "", nbSeries: 1,
        outputInterpretation: "", image: "", replicas: 1,
        groupId: "", groupLabel: "", analysisType: "",
        alignSeries: false,
        config: {best: 0, goodThreshold: 0.5, okThreshold: 0.8},
        series: [{name: "observed", label: "Serie observee", columnName: "value", dataType: "NUMERIC", description: ""}],
        params: [], outputs: [],
        rabbitmq: {
            exchange: "analyse", exchangeType: "topic", routingKey: "",
            queue: "", dlxExchange: "analyse-dlx", dlqQueue: "analyse-dead-letters",
            prefetchCount: 1, maxRetries: 5, retryBaseDelay: 2.0,
        },
    };
}
function emptySeries(i) { return {name: i === 1 ? "expected" : `series_${i}`, label: "", columnName: "value", dataType: "NUMERIC", description: ""}; }
function emptyParam(i) { return {name: `param_${i ?? 0}`, label: "", type: "FLOAT", default: null, min: null, max: null, options: [], description: ""}; }
function emptyOutput(i) { return {name: i === 0 ? "value" : i === 1 ? "delta" : `output_${i}`, label: "", description: "", unit: ""}; }

export default function ModelEditDialog({target, groups, existingIds = [], onClose, onSaved, showToast}) {
    // Normalise image en objet {registry, image, tag, ...}
    function normalizeImage(img) {
        if (typeof img === "string" && img) {
            const m = img.match(/^(.+?)\/(.+):(.+)$/);
            return m ? {registry: m[1], image: m[2], tag: m[3]} : {registry: "", image: img, tag: ""};
        }
        if (!img || typeof img !== "object") return {registry: "", image: "", tag: ""};
        return {...img};
    }

    const isNew = target === "new";
    const isEdit = !isNew && !!target;
    const [loading, setLoading] = React.useState(false);
    const [loadingModel, setLoadingModel] = React.useState(false); // chargement du modèle complet (files)
    const [saveError, setSaveError] = React.useState(null); // message d'erreur persistant
    const [form, setFormState] = React.useState(() => emptyModel());
    const [step, setStep] = React.useState(0);
    const [allModels, setAllModels] = React.useState([]);
    // Le modèle a-t-il été sauvegardé au moins une fois (= existe côté serveur) ?
    const [savedOnce, setSavedOnce] = React.useState(false);

    React.useEffect(() => {
        if (!target) return;
        if (target === "new") {
            setFormState(emptyModel());
            setStep(0);
            setLocked(false);
            setSaveError(null);
            setSavedOnce(false);
        } else {
            const {_rowId, ...d} = target;
            d.image = normalizeImage(d.image);
            if (!d.version && d.image?.tag) d.version = d.image.tag;
            setFormState({...emptyModel(), ...d});
            setLocked(true);
            setStep(2);
            setSaveError(null);
            setSavedOnce(true);
            // Recharger le modèle complet depuis l'API (la liste peut ne pas inclure image.files)
            if (d.id) {
                setLoadingModel(true);
                ApiAnalyticsEngineCtrl.getEngineModel(d.id).then(({data}) => {
                    if (data) {
                        data.image = normalizeImage(data.image);
                        if (!data.version && data.image?.tag) data.version = data.image.tag;
                        setFormState(prev => ({...prev, ...data}));
                    }
                }).finally(() => setLoadingModel(false));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target]);

    React.useEffect(() => {
        if (!isEdit) return;
        ApiAnalyticsEngineCtrl.getEngineModels().then(({data}) => {
            if (!data) return;
            setAllModels(data);
            // allModels chargés pour les registries connus
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, form.id]);

    const knownRegistries = React.useMemo(() => {
        const set = new Set(KNOWN_REGISTRIES);
        allModels.forEach(m => {
            if (typeof m.image === "object" && m.image?.registry) set.add(m.image.registry);
        });
        return Array.from(set).sort();
    }, [allModels]);

    function setField(f, v) { setFormState(p => ({...p, [f]: v})); }
    function setConfig(f, v) { setFormState(p => ({...p, config: {...p.config, [f]: v}})); }
    function setRabbitmq(f, v) { setFormState(p => ({...p, rabbitmq: {...(p.rabbitmq ?? {}), [f]: v}})); }

    // Auto-dériver routingKey depuis l'ID si non renseigné
    React.useEffect(() => {
        if (!form.id) return;
        setFormState(p => {
            const rmq = p.rabbitmq ?? {};
            if (rmq.routingKey) return p;
            return {...p, rabbitmq: {...rmq, routingKey: p.id}};
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.id]);

    // Auto-dériver queue depuis routingKey si non renseigné
    React.useEffect(() => {
        const rk = form.rabbitmq?.routingKey;
        if (!rk) return;
        setFormState(p => {
            const rmq = p.rabbitmq ?? {};
            if (rmq.queue) return p;
            return {...p, rabbitmq: {...rmq, queue: rk}};
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.rabbitmq?.routingKey]);
    function addSeries() { setFormState(f => { const s = [...(f.series ?? []), emptySeries(f.series?.length ?? 0)]; return {...f, series: s, nbSeries: s.length}; }); }
    function removeSeries(si) { setFormState(f => { const s = f.series.filter((_, i) => i !== si); return {...f, series: s, nbSeries: s.length}; }); }
    function updateSeries(si, k, v) { setFormState(f => { const s = [...(f.series ?? [])]; s[si] = {...s[si], [k]: v}; return {...f, series: s}; }); }
    function addParam() { setFormState(f => ({...f, params: [...(f.params ?? []), emptyParam(f.params?.length ?? 0)]})); }
    function removeParam(pi) { setFormState(f => ({...f, params: f.params.filter((_, i) => i !== pi)})); }
    function updateParam(pi, k, v) { setFormState(f => { const p = [...(f.params ?? [])]; p[pi] = {...p[pi], [k]: v}; return {...f, params: p}; }); }
    function addOutput() { setFormState(f => ({...f, outputs: [...(f.outputs ?? []), emptyOutput(f.outputs?.length ?? 0)]})); }
    function removeOutput(oi) { setFormState(f => ({...f, outputs: f.outputs.filter((_, i) => i !== oi)})); }
    function updateOutput(oi, k, v) { setFormState(f => { const o = [...(f.outputs ?? [])]; o[oi] = {...o[oi], [k]: v}; return {...f, outputs: o}; }); }



    // ── Build status polling ──
    const [buildStatus, setBuildStatus] = React.useState(null); // {status, buildId, image, startedAt, finishedAt}
    const [buildLogs, setBuildLogs] = React.useState("");
    const [buildPolling, setBuildPolling] = React.useState(false);
    const buildIntervalRef = React.useRef(null);

    function startBuildPolling(modelId) {
        setBuildPolling(true);
        setBuildLogs("");
        setBuildStatus(null);
        if (buildIntervalRef.current) clearInterval(buildIntervalRef.current);
        buildIntervalRef.current = setInterval(async () => {
            try {
                const {data: st} = await ApiAnalyticsEngineCtrl.getBuildStatus(modelId);
                if (st) setBuildStatus(st);
                const {data: logs} = await ApiAnalyticsEngineCtrl.getLiveBuildLogs(modelId);
                if (logs) setBuildLogs(logs);
                if (st && ["SUCCESS", "FAILED", "TIMEOUT", "DISAPPEARED"].includes(st.status)) {
                    clearInterval(buildIntervalRef.current);
                    buildIntervalRef.current = null;
                    setBuildPolling(false);
                }
            } catch (_) { /* ignore polling errors */ }
        }, 3000);
    }

    function stopBuildPolling() {
        if (buildIntervalRef.current) { clearInterval(buildIntervalRef.current); buildIntervalRef.current = null; }
        setBuildPolling(false);
    }

    React.useEffect(() => { return () => stopBuildPolling(); }, []);

    // Charger le dernier build quand on ouvre l'etape Build
    React.useEffect(() => {
        if (step !== 3 || !form.id) return;
        (async () => {
            const {data: st} = await ApiAnalyticsEngineCtrl.getBuildStatus(form.id);
            if (st) setBuildStatus(st);
            if (st && ["PENDING", "BUILDING"].includes(st.status)) {
                startBuildPolling(form.id);
            } else {
                const {data: logs} = await ApiAnalyticsEngineCtrl.getLiveBuildLogs(form.id);
                if (logs) setBuildLogs(logs);
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, form.id]);

    async function handleSave() {
        setLoading(true);
        setSaveError(null);
        try {
            const img = normalizeImage(form.image);
            if (form.version && !img.tag) img.tag = form.version;
            const payload = {...form, nbSeries: form.series?.length ?? form.nbSeries ?? 1, image: img};
            const {error} = await ApiAnalyticsEngineCtrl.saveEngineModel(payload);
            if (!error) {
                setSavedOnce(true);
                showToast({msg: isNew ? "Modele cree" : "Modele mis a jour", severity: "success"});
                await onSaved();
                // Si le modele a du code (language + files), aller a l'etape Build et lancer le polling
                const hasCode = img.language && img.files && Object.keys(img.files).length > 0;
                if (hasCode) {
                    setStep(3);
                    startBuildPolling(form.id);
                } else {
                    // Pas de code inline — aller à l'étape Build pour voir le statut
                    setStep(3);
                }
            } else {
                // Extraire un message lisible depuis la réponse HTTP
                const raw = error?.response?.data;
                const msg = (typeof raw === "string" && raw)
                    ? raw
                    : raw?.message || raw?.error || raw?.detail
                    || error?.message
                    || "Erreur inconnue";
                const status = error?.response?.status;
                setSaveError(status ? `[HTTP ${status}] ${msg}` : msg);
                showToast({msg: "Echec de la sauvegarde", severity: "error"});
            }
        } catch (e) {
            setSaveError(e?.message ?? "Erreur inattendue");
            showToast({msg: "Echec de la sauvegarde", severity: "error"});
        } finally {
            setLoading(false);
        }
    }

    async function handlePaste() {
        try {
            const text = await navigator.clipboard.readText();
            const parsed = JSON.parse(text);
            const d = Array.isArray(parsed?.models) ? parsed.models[0] : parsed;
            if (!d || typeof d !== "object") { showToast({msg: "JSON invalide", severity: "warning"}); return; }
            const {_rowId, ...clean} = d;
            // Normaliser image string → objet
            clean.image = normalizeImage(clean.image);
            // Synchroniser version depuis le tag si pas de version
            if (!clean.version && clean.image?.tag) clean.version = clean.image.tag;
            // Auto-dériver rabbitmq si absent ou incomplet
            if (!clean.rabbitmq) clean.rabbitmq = {};
            if (!clean.rabbitmq.routingKey && clean.id) clean.rabbitmq.routingKey = clean.id;
            if (!clean.rabbitmq.queue && clean.rabbitmq.routingKey) clean.rabbitmq.queue = clean.rabbitmq.routingKey;
            setFormState(p => ({...emptyModel(), ...p, ...clean}));
            showToast({msg: `Modele "${clean.id ?? "?"}" colle`, severity: "success"});
        } catch (e) {
            showToast({msg: e instanceof SyntaxError ? "JSON invalide" : "Presse-papier inaccessible", severity: "error"});
        }
    }

    const groupOptions = (groups ?? []).map(g => ({id: g.id, label: `${g.id} — ${g.label ?? ""}`}));
    const [locked, setLocked] = React.useState(isEdit); // cadenas : verrouillé par défaut en édition
    const editLocked = isEdit && locked;
    const modelName = form.label || form.id || "Nouveau modele";

    return (
        <Dialog open={!!target} onClose={onClose} maxWidth="lg" fullWidth slotProps={{paper: {sx: {height: "92vh"}}}}>
            {/* ── Header avec nom du modèle ── */}
            <DialogTitle sx={{pb: 1}}>
                <Stack direction="row" alignItems="center" gap={1}>
                    {isNew ? <AddIcon color="primary"/> : <EditIcon color="primary"/>}
                    <Typography variant="h6" fontWeight={700} sx={{flex: 1}}>
                        {modelName}
                    </Typography>
                    {form.version && <Chip label={`v${form.version}`} size="small" color="primary" variant="outlined"/>}
                    {form.groupLabel && <Chip label={form.groupLabel} size="small" variant="outlined" sx={{fontSize: 11}}/>}
                    {isEdit && (
                        <Tooltip title={locked ? "Deverrouiller l'edition des champs" : "Verrouiller les champs (seul le code reste editable)"}>
                            <IconButton size="small" onClick={() => setLocked(!locked)}
                                        sx={{color: locked ? "error.main" : "success.main"}}>
                                {locked ? <LockIcon fontSize="small"/> : <LockOpenIcon fontSize="small"/>}
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Coller JSON">
                        <Button size="small" variant="outlined" color="secondary" startIcon={<ContentPasteIcon fontSize="small"/>}
                                onClick={handlePaste}>JSON</Button>
                    </Tooltip>
                </Stack>
            </DialogTitle>

            {/* ── Stepper ── */}
            <Box sx={{px: 3, pb: 1}}>
                <Stepper activeStep={step} alternativeLabel>
                    {STEPS.map((label, i) => {
                        const isBuildStep = i === 3;
                        const disabled = isBuildStep && !savedOnce;
                        return (
                            <Step key={label} completed={i < step}
                                  sx={{cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1}}
                                  onClick={() => { if (!disabled) setStep(i); }}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        );
                    })}
                </Stepper>
            </Box>

            <Divider/>
            <DialogContent sx={{overflowY: "auto", display: "flex", flexDirection: "column", gap: 2, pt: 2}}>

                {/* ═══ ÉTAPE 0 : IDENTITÉ (Groupe + Modèle) ═══ */}
                {step === 0 && (
                    <Stack spacing={2}>
                        {/* Groupe — défini par le type d'analyse */}
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                            <Autocomplete freeSolo options={ANALYSIS_TYPES} sx={{flex: 1}} disabled={editLocked}
                                value={form.analysisType ?? ""}
                                onChange={(_e, v) => {
                                    const type = typeof v === "string" ? v : "";
                                    setField("analysisType", type);
                                    const mapping = ANALYSIS_TYPE_MAP[type];
                                    if (mapping) {
                                        setField("groupId", mapping.groupId);
                                        setField("groupLabel", mapping.groupLabel);
                                    }
                                }}
                                onInputChange={(_e, v) => {
                                    setField("analysisType", v ?? "");
                                    const mapping = ANALYSIS_TYPE_MAP[v];
                                    if (mapping) {
                                        setField("groupId", mapping.groupId);
                                        setField("groupLabel", mapping.groupLabel);
                                    }
                                }}
                                renderInput={params => <TextField {...params} label="Type d'analyse" size="small" fullWidth
                                    helperText={form.groupId ? `Groupe : ${form.groupLabel || form.groupId}` : "Choisissez un type"}/>}/>
                        </Stack>
                        <Divider/>
                        {/* Modèle */}
                        <Stack direction="row" spacing={2}>
                            <TextField label="ID" size="small" sx={{flex: 1}} value={form.id ?? ""}
                                       onChange={e => setField("id", toDnsLabel(e.target.value))} disabled={isEdit}
                                       error={isNew && !!form.id && (existingIds.includes(form.id) || !DNS_LABEL_RE.test(form.id))}
                                       helperText={
                                           isNew && !!form.id && existingIds.includes(form.id) ? "ID existe deja"
                                           : isNew && !!form.id && !DNS_LABEL_RE.test(form.id) ? "Minuscules, chiffres et tirets uniquement"
                                           : "Ex: anomaly-iqr (auto-formatte)"
                                       }/>
                            <TextField label="Libelle" size="small" sx={{flex: 1}} value={form.label ?? ""}
                                       onChange={e => setField("label", e.target.value)} disabled={isEdit}/>
                            <TextField label="Version" size="small" sx={{flex: 0.5}} disabled={editLocked}
                                       value={form.version ?? (typeof form.image === "object" ? form.image?.tag || "" : "")}
                                       onChange={e => {
                                           const v = e.target.value.toLowerCase().replace(/[^a-z0-9.\-]/g, "");
                                           setField("version", v);
                                           const img = typeof form.image === "object" && form.image ? {...form.image} : {};
                                           img.tag = v;
                                           setField("image", img);
                                       }}
                                       error={!!form.version && !VERSION_RE.test(form.version)}
                                       placeholder="1.0.0" helperText={
                                           !!form.version && !VERSION_RE.test(form.version)
                                               ? "Minuscules, chiffres, points et tirets"
                                               : "= tag Docker (auto-formatte)"
                                       }/>
                        </Stack>
                        <Stack direction="row" spacing={2}>
                            <Autocomplete freeSolo options={knownRegistries} sx={{flex: 2}} disabled={editLocked}
                                value={typeof form.image === "object" ? (form.image?.registry || "") : ""}
                                onChange={(_e, v) => { const img = typeof form.image === "object" && form.image ? {...form.image} : {}; img.registry = typeof v === "string" ? v : ""; setField("image", img); }}
                                onInputChange={(_e, v) => { const img = typeof form.image === "object" && form.image ? {...form.image} : {}; img.registry = v ?? ""; setField("image", img); }}
                                renderInput={params => <TextField {...params} label="Registry" size="small" placeholder="registry.gitlab.com/blange/inria2025"/>}/>
                            <TextField label="Image" size="small" sx={{flex: 1}} disabled={editLocked}
                                       placeholder="analytics-mon-modele"
                                       value={typeof form.image === "object" ? (form.image?.image || "") : ""}
                                       onChange={e => { const img = typeof form.image === "object" && form.image ? {...form.image} : {}; img.image = e.target.value; setField("image", img); }}/>
                        </Stack>
                        <TextField label="Description" size="small" fullWidth multiline rows={2} value={form.description ?? ""}
                                   onChange={e => setField("description", e.target.value)} disabled={editLocked}/>
                        <TextField label="Interpretation du resultat" size="small" fullWidth multiline rows={2} value={form.outputInterpretation ?? ""}
                                   onChange={e => setField("outputInterpretation", e.target.value)} disabled={editLocked}/>
                        <Divider/>
                        {/* RabbitMQ */}
                        <Accordion disableGutters defaultExpanded={false}
                                   sx={{border: "1px solid", borderColor: "divider", borderRadius: 1.5, "&:before": {display: "none"}, boxShadow: "none"}}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon/>} sx={{minHeight: 36, "& .MuiAccordionSummary-content": {my: 0.5}}}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase">
                                    RabbitMQ
                                    {form.rabbitmq?.routingKey && (
                                        <Chip label={form.rabbitmq.routingKey} size="small" variant="outlined" sx={{ml: 1, fontSize: 10, height: 18}}/>
                                    )}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{pt: 0}}>
                                <Stack spacing={2}>
                                    <Stack direction="row" spacing={2}>
                                        <TextField label="Exchange" size="small" sx={{flex: 2}} disabled={editLocked}
                                                   value={form.rabbitmq?.exchange ?? "analyse"}
                                                   onChange={e => setRabbitmq("exchange", e.target.value)}
                                                   helperText="Exchange principal (defaut: analyse)"/>
                                        <TextField label="Type d'exchange" size="small" sx={{flex: 1}} disabled={editLocked}
                                                   select SelectProps={{native: true}}
                                                   value={form.rabbitmq?.exchangeType ?? "topic"}
                                                   onChange={e => setRabbitmq("exchangeType", e.target.value)}>
                                            {["topic", "direct", "fanout", "headers"].map(t => (
                                                <option key={t} value={t}>{t}</option>
                                            ))}
                                        </TextField>
                                    </Stack>
                                    <Stack direction="row" spacing={2}>
                                        <TextField label="Routing key" size="small" sx={{flex: 1}} disabled={editLocked}
                                                   value={form.rabbitmq?.routingKey ?? ""}
                                                   onChange={e => setRabbitmq("routingKey", e.target.value)}
                                                   placeholder={form.id || "= ID du modele"}
                                                   helperText="Cle de routage (vide = ID du modele)"/>
                                        <TextField label="Queue" size="small" sx={{flex: 1}} disabled={editLocked}
                                                   value={form.rabbitmq?.queue ?? ""}
                                                   onChange={e => setRabbitmq("queue", e.target.value)}
                                                   placeholder={form.rabbitmq?.routingKey || form.id || "= routing key"}
                                                   helperText="Nom de la queue (vide = routing key)"/>
                                    </Stack>
                                    <Stack direction="row" spacing={2}>
                                        <TextField label="DLX Exchange" size="small" sx={{flex: 2}} disabled={editLocked}
                                                   value={form.rabbitmq?.dlxExchange ?? "analyse-dlx"}
                                                   onChange={e => setRabbitmq("dlxExchange", e.target.value)}
                                                   helperText="Exchange dead-letter (defaut: analyse-dlx)"/>
                                        <TextField label="DLQ Queue" size="small" sx={{flex: 2}} disabled={editLocked}
                                                   value={form.rabbitmq?.dlqQueue ?? "analyse-dead-letters"}
                                                   onChange={e => setRabbitmq("dlqQueue", e.target.value)}
                                                   helperText="Queue dead-letter (defaut: analyse-dead-letters)"/>
                                    </Stack>
                                    <Stack direction="row" spacing={2}>
                                        <TextField label="Prefetch count" type="number" size="small" sx={{flex: 1}} disabled={editLocked}
                                                   value={form.rabbitmq?.prefetchCount ?? 1}
                                                   onChange={e => setRabbitmq("prefetchCount", parseInt(e.target.value) || 1)}
                                                   helperText="Nb messages prefetches (defaut: 1)"/>
                                        <TextField label="Max retries" type="number" size="small" sx={{flex: 1}} disabled={editLocked}
                                                   value={form.rabbitmq?.maxRetries ?? 5}
                                                   onChange={e => setRabbitmq("maxRetries", parseInt(e.target.value) || 0)}
                                                   helperText="Tentatives avant DLQ (defaut: 5)"/>
                                        <TextField label="Retry base delay (s)" type="number" size="small" sx={{flex: 1}} disabled={editLocked}
                                                   value={form.rabbitmq?.retryBaseDelay ?? 2.0}
                                                   onChange={e => setRabbitmq("retryBaseDelay", parseFloat(e.target.value) || 0)}
                                                   inputProps={{step: 0.5}}
                                                   helperText="Delai backoff exponentiel (defaut: 2s)"/>
                                    </Stack>
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    </Stack>
                )}

                {/* ═══ ÉTAPE 1 : CONFIGURATION ═══ */}
                {step === 1 && (
                    <Stack spacing={3}>
                        <Paper variant="outlined" sx={{p: 2, borderRadius: 1.5}}>
                            <Typography variant="caption" fontWeight={700} color="text.secondary" mb={1} display="block" textTransform="uppercase">Score</Typography>
                            <Stack direction="row" spacing={2}>
                                <TextField label="Best" type="number" size="small" sx={{flex: 1}} disabled={editLocked}
                                           value={form.config?.best ?? 0} onChange={e => setConfig("best", parseFloat(e.target.value) || 0)}/>
                                <TextField label="Seuil bon" type="number" size="small" sx={{flex: 1}} disabled={editLocked}
                                           value={form.config?.goodThreshold ?? 0.5} onChange={e => setConfig("goodThreshold", parseFloat(e.target.value) || 0)}/>
                                <TextField label="Seuil OK" type="number" size="small" sx={{flex: 1}} disabled={editLocked}
                                           value={form.config?.okThreshold ?? 0.8} onChange={e => setConfig("okThreshold", parseFloat(e.target.value) || 0)}/>
                            </Stack>
                        </Paper>
                        <FormControlLabel
                            control={<Switch checked={!!form.alignSeries} disabled={editLocked}
                                             onChange={e => setField("alignSeries", e.target.checked)}/>}
                            label="Aligner les series par date (intersection)"
                            sx={{ml: 0.5}}
                        />
                        <Paper variant="outlined" sx={{p: 2, borderRadius: 1.5}}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase">Series ({(form.series ?? []).length})</Typography>
                                {!editLocked && <Button size="small" startIcon={<AddIcon/>} onClick={addSeries}>Ajouter</Button>}
                            </Stack>
                            <Stack spacing={1}>
                                {(form.series ?? []).map((s, si) => (
                                    <SeriesRow key={si} series={s} index={si} disabled={editLocked}
                                               canDelete={(form.series ?? []).length > 1}
                                               allNames={(form.series ?? []).map(x => x.name)}
                                               onRemove={() => removeSeries(si)} onUpdate={(k, v) => updateSeries(si, k, v)}/>
                                ))}
                            </Stack>
                        </Paper>
                        <Paper variant="outlined" sx={{p: 2, borderRadius: 1.5}}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase">Parametres ({(form.params ?? []).length})</Typography>
                                {!editLocked && <Button size="small" startIcon={<AddIcon/>} onClick={addParam}>Ajouter</Button>}
                            </Stack>
                            <Stack spacing={1}>
                                {(form.params ?? []).map((p, pi) => (
                                    <ParamRow key={pi} param={p} index={pi} allNames={(form.params ?? []).map(x => x.name)}
                                              disabled={editLocked}
                                              onRemove={() => removeParam(pi)}
                                              onUpdate={(k, v) => updateParam(pi, k, v)}/>
                                ))}
                                {!(form.params ?? []).length && <Typography variant="caption" color="text.disabled" pl={1}>Aucun parametre</Typography>}
                            </Stack>
                        </Paper>
                        <Paper variant="outlined" sx={{p: 2, borderRadius: 1.5}}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase">Sorties ({(form.outputs ?? []).length})</Typography>
                                {!editLocked && <Button size="small" startIcon={<AddIcon/>} onClick={addOutput}>Ajouter</Button>}
                            </Stack>
                            <Stack spacing={1}>
                                {(form.outputs ?? []).map((o, oi) => (
                                    <OutputRow key={oi} output={o} index={oi} allNames={(form.outputs ?? []).map(x => x.name)}
                                               disabled={editLocked}
                                               onRemove={() => removeOutput(oi)}
                                               onUpdate={(k, v) => updateOutput(oi, k, v)}/>
                                ))}
                                {!(form.outputs ?? []).length && <Typography variant="caption" color="text.disabled" pl={1}>Aucune sortie</Typography>}
                            </Stack>
                        </Paper>
                    </Stack>
                )}

                {/* ═══ ÉTAPE 2 : CODE SOURCE ═══ */}
                {step === 2 && loadingModel && (
                    <Stack sx={{flex: 1, minHeight: 0, justifyContent: "center", alignItems: "center", gap: 2}}>
                        <LinearProgress sx={{width: "60%", borderRadius: 2}}/>
                        <Typography variant="body2" color="text.secondary">
                            Chargement du code source...
                        </Typography>
                    </Stack>
                )}
                {step === 2 && !loadingModel && (
                    <Stack sx={{flex: 1, minHeight: 0}}>
                        <ImageOrCodeSection form={form} setField={setField}
                                            isEdit={isEdit}/>
                    </Stack>
                )}

                {/* ═══ ÉTAPE 3 : BUILD ═══ */}
                {step === 3 && (
                    <Stack spacing={2} sx={{flex: 1, minHeight: 0}}>
                        {/* Status banner */}
                        <Paper variant="outlined" sx={{p: 2, borderRadius: 1.5}}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                {(() => {
                                    const cfg = BUILD_STATUS_CONFIG[buildStatus?.status] ?? BUILD_STATUS_CONFIG.NONE;
                                    return (
                                        <>
                                            <Box sx={{color: cfg.color}}>{cfg.icon}</Box>
                                            <Stack sx={{flex: 1}}>
                                                <Typography variant="subtitle2" fontWeight={700} sx={{color: cfg.color}}>
                                                    {cfg.label}
                                                </Typography>
                                                {buildStatus?.image && (
                                                    <Typography variant="caption" color="text.secondary">{buildStatus.image}</Typography>
                                                )}
                                                {buildStatus?.startedAt && (
                                                    <Typography variant="caption" color="text.disabled">
                                                        Debut : {buildStatus.startedAt}
                                                        {buildStatus.finishedAt && ` — Fin : ${buildStatus.finishedAt}`}
                                                    </Typography>
                                                )}
                                            </Stack>
                                            {buildPolling && <CircularProgress size={20}/>}
                                            {!buildPolling && form.id && (
                                                <Tooltip title="Rafraichir">
                                                    <IconButton size="small" onClick={() => startBuildPolling(form.id)}>
                                                        <RefreshIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </>
                                    );
                                })()}
                            </Stack>
                        </Paper>
                        {/* Live logs */}
                        <Paper variant="outlined" sx={{
                            flex: 1, minHeight: 200, borderRadius: 1.5, overflow: "hidden",
                            bgcolor: "#0d1117",
                        }}>
                            <AnsiText
                                placeholder="En attente des logs..."
                                sx={{ maxHeight: "100%", overflow: "auto", borderRadius: 1.5 }}
                            >
                                {buildLogs}
                            </AnsiText>
                        </Paper>
                    </Stack>
                )}

            </DialogContent>
            <Collapse in={!!saveError}>
                <Alert severity="error" onClose={() => setSaveError(null)}
                       sx={{mx: 3, mb: 1, borderRadius: 1, fontSize: 13, wordBreak: "break-word"}}>
                    <strong>Echec de la sauvegarde&nbsp;: </strong>{saveError}
                </Alert>
            </Collapse>
            <Divider/>
            <DialogActions sx={{px: 3, py: 1.5, justifyContent: "space-between"}}>
                <Stack direction="row" gap={1}>
                    <Button onClick={onClose} disabled={loading}>Annuler</Button>
                </Stack>
                <Stack direction="row" gap={1}>
                    {step > 0 && (
                        <Button startIcon={<ArrowBackIcon/>} onClick={() => setStep(s => s - 1)}>
                            Precedent
                        </Button>
                    )}
                    {step < 2 && (
                        <Button variant="outlined" endIcon={<ArrowForwardIcon/>} onClick={() => setStep(s => s + 1)}>
                            Suivant
                        </Button>
                    )}
                    {step === 2 && (
                        <Button variant="contained" onClick={handleSave}
                                disabled={loading || !form.id || !form.label || !form.groupId || (isNew && existingIds.includes(form.id))}
                                startIcon={loading ? <CircularProgress size={16} color="inherit"/> : isNew ? <AddIcon/> : <EditIcon/>}>
                            {isNew ? "Creer" : "Enregistrer"}
                        </Button>
                    )}
                    {step === 3 && (
                        <Button variant="contained" onClick={() => { stopBuildPolling(); onClose(); }}>
                            Fermer
                        </Button>
                    )}
                </Stack>
            </DialogActions>
        </Dialog>
    );
}
